import React, { useState } from 'react';
import { Sparkles, X, Copy, Check, RefreshCw, Wand2, Globe, FileSearch } from 'lucide-react';
import axiosClient from '../api/axiosClient';
import toast from 'react-hot-toast';

export default function AiCopilotDrawer({ isOpen, onClose }) {
  const [action, setAction] = useState('rewrite');
  const [prompt, setPrompt] = useState('');
  const [targetLanguage, setTargetLanguage] = useState('Spanish');
  const [output, setOutput] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  if (!isOpen) return null;

  const handleGenerate = async () => {
    if (!prompt && action !== 'generate_seo') {
      toast.error('Please enter a prompt or topic.');
      return;
    }
    setLoading(true);
    try {
      const res = await axiosClient.post('/ai/generate', {
        action,
        prompt,
        targetLanguage,
      });
      setOutput(res.data.output);
      toast.success('AI content generated!');
    } catch (err) {
      toast.error('AI generation failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(output);
    setCopied(true);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="fixed inset-0 z-50 overflow-hidden bg-slate-950/60 backdrop-blur-sm">
      <div className="absolute inset-y-0 right-0 max-w-full flex pl-10">
        <div className="w-screen max-w-md bg-slate-900 border-l border-slate-800 text-slate-100 shadow-2xl flex flex-col">
          {/* Header */}
          <div className="p-6 border-b border-slate-800 flex items-center justify-between bg-slate-950">
            <div className="flex items-center space-x-3">
              <div className="p-2 rounded-xl bg-purple-600/20 border border-purple-500/30 text-purple-400">
                <Sparkles size={20} />
              </div>
              <div>
                <h3 className="font-bold text-lg text-white">AI Content Copilot</h3>
                <p className="text-xs text-slate-400">Generate, rewrite & optimize website copy instantly</p>
              </div>
            </div>
            <button onClick={onClose} className="p-1 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white">
              <X size={20} />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 p-6 space-y-6 overflow-y-auto">
            {/* Action selection */}
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">Select Task</label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { id: 'rewrite', label: 'Rewrite & Polish', icon: Wand2 },
                  { id: 'generate_seo', label: 'Generate SEO Meta', icon: FileSearch },
                  { id: 'generate_faqs', label: 'Draft FAQs', icon: Sparkles },
                  { id: 'translate', label: 'Translate Copy', icon: Globe },
                ].map((item) => {
                  const Icon = item.icon;
                  return (
                    <button
                      key={item.id}
                      onClick={() => setAction(item.id)}
                      className={`flex items-center space-x-2 p-3 rounded-xl border text-xs font-semibold transition-all ${
                        action === item.id
                          ? 'bg-purple-600/20 border-purple-500 text-purple-300 shadow-lg shadow-purple-900/30'
                          : 'bg-slate-800/40 border-slate-800 text-slate-400 hover:bg-slate-800 hover:text-white'
                      }`}
                    >
                      <Icon size={16} />
                      <span>{item.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Language input if translate */}
            {action === 'translate' && (
              <div>
                <label className="block text-xs font-bold text-slate-400 mb-1">Target Language</label>
                <select
                  value={targetLanguage}
                  onChange={(e) => setTargetLanguage(e.target.value)}
                  className="w-full p-2.5 rounded-xl border border-slate-800 bg-slate-950 text-slate-200 text-sm focus:ring-2 focus:ring-purple-500"
                >
                  <option value="Spanish">Spanish</option>
                  <option value="French">French</option>
                  <option value="German">German</option>
                  <option value="Japanese">Japanese</option>
                  <option value="Arabic">Arabic</option>
                </select>
              </div>
            )}

            {/* Prompt input */}
            <div>
              <label className="block text-xs font-bold text-slate-400 mb-1">Context / Prompt</label>
              <textarea
                rows={4}
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="Enter raw text, page topic, or service feature to enhance..."
                className="w-full p-3 rounded-xl border border-slate-800 bg-slate-950 text-slate-200 text-sm focus:ring-2 focus:ring-purple-500 focus:outline-none"
              />
            </div>

            <button
              onClick={handleGenerate}
              disabled={loading}
              className="w-full py-3 rounded-xl font-bold text-sm bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white shadow-lg shadow-purple-600/30 flex items-center justify-center space-x-2 transition-all"
            >
              {loading ? <RefreshCw className="animate-spin" size={18} /> : <Sparkles size={18} />}
              <span>{loading ? 'Processing...' : 'Generate with AI'}</span>
            </button>

            {/* Output Display */}
            {output && (
              <div className="pt-4 border-t border-slate-800">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-bold uppercase text-slate-400">Generated Response</span>
                  <button
                    onClick={handleCopy}
                    className="flex items-center space-x-1 text-xs text-purple-400 hover:text-purple-300 font-semibold"
                  >
                    {copied ? <Check size={14} /> : <Copy size={14} />}
                    <span>{copied ? 'Copied' : 'Copy'}</span>
                  </button>
                </div>
                <div className="p-4 rounded-xl border border-slate-800 bg-slate-950 font-mono text-xs text-slate-300 whitespace-pre-wrap max-h-60 overflow-y-auto">
                  {output}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
